import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Container,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Dialog,
  DialogContent,
  useScrollTrigger,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import mpactLogo from '../images/MPact_logo.png';
import propvizerLogo from '../images/propvizer.png';
import sentientLogo from '../images/Sentient Co Logo.jpeg';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubmissionWindow } from '../hooks/useSubmissionWindow';
import LoginForm from './LoginForm';
import LanguageToggle from './LanguageToggle';
import { ADMIN_EMAILS } from '../constants';

// Evanston partner logos: full wordmark on large screens, icon-only crop on
// medium screens (the icon sits at the left edge of each image; Sentient is
// already square, so its "crop" shows the whole mark)
const EVANSTON_LOGOS = [
  {
    name: 'propvizer',
    src: propvizerLogo,
    alt: 'Propvizer Logo',
    href: 'https://www.propvizer.co/',
    fullHeight: 60,
    // The wordmark starts ~40px in at this scale; a narrower window keeps it out
    iconWidth: 38,
  },
  {
    name: 'mpact',
    src: mpactLogo,
    alt: 'MPact Logo',
    href: 'https://mpactcollective.com/',
    fullHeight: 26,
    iconWidth: 44,
  },
  {
    name: 'sentient',
    src: sentientLogo,
    alt: 'Sentient Co Logo',
    href: 'https://sentientco.org/',
    fullHeight: 54,
    iconWidth: 44,
  },
];

const LOGO_ICON_SIZE = 44;

// Elevation on scroll
function ElevationScroll(props: { children: React.ReactElement<any, any> }) {
  const { children } = props;
  const location = useLocation();
  const isAboutPage = location.pathname === '/about' || location.pathname === '/';
  
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    sx: {
      // When scrolled down, use dark background; when at top, be transparent only on about page
      backgroundColor: trigger 
        ? 'primary.dark'  // Dark blue-purple when scrolled
        : isAboutPage 
          ? 'transparent' 
          : 'primary.dark', // Dark blue-purple on other pages
      color: 'white', // Always white text
      transition: 'all 0.3s ease-in-out',
      boxShadow: trigger ? '0 2px 10px rgba(0,0,0,0.2)' : 'none',
    },
    elevation: trigger ? 4 : 0,
  });
}

const Header: React.FC = () => {
  const { isAuthenticated, currentUser, logout } = useAuth();
  const { tenantConfig, isLoading: tenantLoading } = useTenant();
  const { t, isToggleEnabled } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [accountAnchorEl, setAccountAnchorEl] = useState<null | HTMLElement>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [isTransparent, setIsTransparent] = useState(true);
  const open = Boolean(anchorEl);
  const accountMenuOpen = Boolean(accountAnchorEl);
  const location = useLocation();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isXLScreen = useMediaQuery(theme.breakpoints.up('xl'));

  // Determine if header should be transparent based on the page
  useEffect(() => {
    setIsTransparent(location.pathname === '/about' || location.pathname === '/');
  }, [location]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAccountAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountAnchorEl(null);
  };

  const handleLoginClick = () => {
    handleAccountMenuClose();
    setLoginDialogOpen(true);
  };

  const handleLoginDialogClose = () => {
    setLoginDialogOpen(false);
  };

  const handleLogout = async () => {
    handleAccountMenuClose();
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const { isClosed: submissionsClosed } = useSubmissionWindow();

  const isCurrentUserAdmin = (): boolean => {
    return !!currentUser?.email && ADMIN_EMAILS.includes(currentUser.email);
  };

  return (
    <>
      <ElevationScroll>
        <AppBar 
          position="fixed" 
          sx={{ 
            zIndex: 1200,
            backgroundColor: isTransparent ? 'transparent' : 'primary.dark',
            color: 'white', // Always white text
            boxShadow: isTransparent ? 'none' : '0 2px 10px rgba(0,0,0,0.1)',
          }}
        >
          <Box sx={{ 
            width: '100%',
            // On xl screens use the full width — the fixed 1400px cap squeezed
            // the title once the Spanish nav (plus admin button) grew
            maxWidth: isXLScreen ? '100%' : isLargeScreen ? '1200px' : '100%',
            mx: 'auto', // Center the content
            px: { xs: 2, sm: 3, md: 4 } // Reduced padding
          }}>
            <Toolbar sx={{
              py: { xs: 1, md: 1.5 },
              display: 'flex',
              alignItems: 'center',
              px: 0,
              minHeight: { xs: 56, md: 64 }, // Ensure consistent height
            }}>
              {/* Logo(s) - Left zone, never shrinks */}
              <Box
                data-testid="header-logos"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  justifyContent: 'flex-start',
                  pr: { xs: 1, md: 3 }, // Add right padding for spacing
                  gap: 2, // Space between logos
                }}
              >
                {tenantConfig?.tenant_id === 'evanston' ? (
                  // Evanston: full wordmarks on lg+, icon crops on sm-lg, nothing on xs
                  <>
                    {EVANSTON_LOGOS.map((logo) => (
                      <Box
                        key={logo.name}
                        component="a"
                        href={logo.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: { xs: 'none', sm: 'block' },
                          transition: 'opacity 0.2s ease-in-out',
                          '&:hover': { opacity: 0.8 }
                        }}
                      >
                        <Box
                          component="img"
                          data-testid={`logo-full-${logo.name}`}
                          src={logo.src}
                          alt={logo.alt}
                          sx={{
                            display: { xs: 'none', xl: 'block' },
                            height: logo.fullHeight,
                          }}
                        />
                        <Box
                          data-testid={`logo-icon-${logo.name}`}
                          sx={{
                            display: { xs: 'block', xl: 'none' },
                            width: logo.iconWidth,
                            height: LOGO_ICON_SIZE,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            component="img"
                            src={logo.src}
                            alt={logo.alt}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              objectPosition: 'left',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </>
                ) : (
                  // Other tenants: Show MPact logo linking to home
                  <Box 
                    component={RouterLink} 
                    to="/"
                    sx={{ 
                      display: { xs: 'none', sm: 'block' },
                      textDecoration: 'none',
                      transition: 'opacity 0.2s ease-in-out',
                      '&:hover': { opacity: 0.8 }
                    }}
                  >
                    <Box 
                      component="img" 
                      src={mpactLogo} 
                      alt="MPact Logo" 
                      sx={{ height: 36 }} 
                    />
                  </Box>
                )}
              </Box>

              {/* Title - Center zone; shrinks and truncates instead of overlapping */}
              <Box
                data-testid="header-title"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  px: { xs: 1, md: 2 },
                }}
              >
                {/* Desktop Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: { xs: 'none', md: 'block' }
                  }}
                >
                  {tenantLoading ? t.header.loading : (tenantConfig?.tenant_name || 'Community Project')}
                </Typography>

                {/* Mobile Title - centered */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: { xs: 'block', md: 'none' }
                  }}
                >
                  {tenantLoading ? t.header.loading : (
                    tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' :
                    tenantConfig?.tenant_id === 'severance' ? 'Severance Project' :
                    'Community Project'
                  )}
                </Typography>
              </Box>

              {/* Desktop Navigation - Right zone, never shrinks */}
              <Box data-testid="header-desktop-nav" sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexShrink: 0,
                gap: 1,
                pl: { xs: 1, md: 3 }, // Add left padding for spacing
              }}>
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/about"
                  sx={{
                    py: 1,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    borderBottom: location.pathname === '/about' ? '2px solid' : '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderBottom: '2px solid',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {t.header.about}
                </Button>
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/home"
                  sx={{
                    py: 1,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    borderBottom: location.pathname === '/home' ? '2px solid' : '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderBottom: '2px solid',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {t.header.ideas}
                </Button>
                {!submissionsClosed && (
                  <Button 
                    variant="outlined"
                    component={RouterLink} 
                    to="/submit"
                    sx={{
                      ml: 1,
                      px: 2,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      borderColor: 'white',
                      color: 'white',
                      textAlign: 'center',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {t.header.submitIdea}
                  </Button>
                )}
                
                {isCurrentUserAdmin() && (
                  <Button
                    component={RouterLink}
                    to="/admin"
                    color="inherit"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      mx: 1,
                    }}
                  >
                    {t.header.admin}
                  </Button>
                )}

                <LanguageToggle />

                {/* Account Button */}
                <IconButton
                  color="inherit"
                  onClick={handleAccountMenu}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  {isAuthenticated && currentUser ? (
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: 'secondary.main', 
                        fontSize: '0.9rem' 
                      }}
                    >
                      {currentUser.email ? currentUser.email[0].toUpperCase() : 'U'}
                    </Avatar>
                  ) : (
                    <AccountCircleIcon />
                  )}
                </IconButton>
                <Menu
                  anchorEl={accountAnchorEl}
                  open={accountMenuOpen}
                  onClose={handleAccountMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  {isAuthenticated && currentUser ? (
                    <>
                      <MenuItem key="email" disabled>
                        <Typography variant="body2">{currentUser.email}</Typography>
                      </MenuItem>
                      <Divider key="divider" />
                      <MenuItem key="logout" onClick={handleLogout}>
                        <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                        {t.header.logout}
                      </MenuItem>
                    </>
                  ) : (
                    <MenuItem onClick={handleLoginClick}>
                      <LoginIcon fontSize="small" sx={{ mr: 1 }} />
                      {t.header.loginSignUp}
                    </MenuItem>
                  )}
                </Menu>
              </Box>

              {/* Mobile Navigation - Right zone, never shrinks */}
              <Box sx={{
                display: { xs: 'flex', md: 'none' },
                flexShrink: 0,
                justifyContent: 'flex-end',
                alignItems: 'center',
                pl: { xs: 1, sm: 2 }, // Add left padding for spacing
              }}>
                {/* Account Button on Mobile */}
                <IconButton
                  color="inherit"
                  onClick={handleAccountMenu}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  {isAuthenticated && currentUser ? (
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: 'secondary.main', 
                        fontSize: '0.9rem' 
                      }}
                    >
                      {currentUser.email ? currentUser.email[0].toUpperCase() : 'U'}
                    </Avatar>
                  ) : (
                    <AccountCircleIcon />
                  )}
                </IconButton>
              
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={open}
                  onClose={handleClose}
                >
                  <MenuItem 
                    component={RouterLink} 
                    to="/about" 
                    onClick={handleClose}
                  >
                    {t.header.about}
                  </MenuItem>
                  <MenuItem 
                    component={RouterLink} 
                    to="/home" 
                    onClick={handleClose}
                  >
                    {t.header.ideas}
                  </MenuItem>
                  {!submissionsClosed && (
                    <MenuItem 
                      component={RouterLink} 
                      to="/submit" 
                      onClick={handleClose}
                      sx={{ color: 'secondary.main', fontWeight: 500 }}
                    >
                      {t.header.submitIdea}
                    </MenuItem>
                  )}
                  {isToggleEnabled && <Divider />}
                  {isToggleEnabled && (
                  <Box
                    data-testid="mobile-menu-language-toggle"
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      px: 2,
                      py: 1,
                      // The toggle inherits the header's white text; the menu is light
                      color: 'text.primary',
                      '& .MuiToggleButton-root': {
                        borderColor: 'divider',
                        '&.Mui-selected': {
                          backgroundColor: 'action.selected',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          },
                        },
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      },
                    }}
                  >
                    <LanguageToggle />
                  </Box>
                  )}
                </Menu>
              </Box>
            </Toolbar>
          </Box>
        </AppBar>
      </ElevationScroll>
      
      {/* Login Dialog */}
      <Dialog 
        open={loginDialogOpen} 
        onClose={handleLoginDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <LoginForm onClose={handleLoginDialogClose} />
        </DialogContent>
      </Dialog>
      
      {/* Only add the spacer on non-About pages */}
      {!isTransparent && <Toolbar />}
    </>
  );
};

export default Header; 